import last from 'lodash/last';
import { v1 as uuidv1 } from 'uuid';
import type { WinStreakConfig } from './types';
import type { WinStreakState } from './WinStreakState';
import type { ResourceMap } from '../../app/resources/types';
import { winStreakConfigs } from './winStreakConfigs';
import { EventConditionType } from '../../app/state/events/types';
import type { WinStreakDependencies, WinStreakObservable } from './WinStreakController';

export function getWinStreakEndTime(state: WinStreakState): number {
  return state.eventStartDate + state.config.duration;
}

export function getWinStreakMinLevel(): number {
  const minLevel = winStreakConfigs.reduce((acc, config) => {
    const levelReachCondition = config.conditions.find(
      (condition) => condition.type === EventConditionType.ReachedLevel,
    );
    if (levelReachCondition) {
      return Math.min(acc, levelReachCondition.level);
    }
    return acc;
  }, Infinity);

  return minLevel === Infinity ? 0 : minLevel;
}

export function startWinStreakEvent(observable: WinStreakObservable, config: WinStreakConfig): WinStreakState {
  const winStreakState = {
    id: `winStreak_${config.eventId}_${uuidv1()}`,
    config,
    currentProgress: 0,
    eventStartDate: Date.now(),
    finished: false,
    lastSeenProgress: null,
    lastOpenedChestIndex: -1,
  };

  observable.updateValue({
    winStreakActiveEvent: winStreakState,
  });

  return winStreakState;
}

export function getCurrentChestProgressVisual(
  state: WinStreakState,
  levelPosition: number = state.currentProgress,
): { current: number; max: number } {
  const chest = state.config.chests[state.lastOpenedChestIndex + 1];
  if (!chest) {
    return { current: 1, max: 1 };
  }

  const { level } = chest;
  const previousChestLevel = state.config.chests[state.lastOpenedChestIndex]?.level ?? 0;

  return {
    current: levelPosition - previousChestLevel,
    max: level - previousChestLevel,
  };
}

export function getCurrentChestProgressByLevel(
  state: WinStreakState,
  levelPosition: number = state.currentProgress,
): { current: number; max: number } {
  const lastChestLevel = state.config.chests.findLast((chest) => chest.level <= levelPosition)?.level ?? 0;

  const nextChestLevel =
    (state.config.chests.find((chest) => chest.level > levelPosition) ?? last(state.config.chests))?.level ?? 100;

  return {
    current: levelPosition - lastChestLevel,
    max: nextChestLevel - lastChestLevel,
  };
}

export function updateWinStreakActivity(dependencies: WinStreakDependencies): void {
  const { observable, isConditionMet } = dependencies;

  const winStreakState = observable.getValue().winStreakActiveEvent;
  if (!winStreakState) return;

  const allConditionsFulfilled = winStreakState.config.conditions.every((condition) => isConditionMet(condition));
  if (Date.now() < getWinStreakEndTime(winStreakState) && allConditionsFulfilled) return;

  observable.updateValue({
    winStreakActiveEvent: {
      ...winStreakState,
      finished: true,
    },
  });
}

export function winStreakLoseLevel(observable: WinStreakObservable): void {
  const winStreakState = observable.getValue().winStreakActiveEvent;
  if (!winStreakState || winStreakState.finished) return;

  observable.updateValue({
    winStreakActiveEvent: {
      ...winStreakState,
      currentProgress: winStreakState.currentProgress - getCurrentChestProgressByLevel(winStreakState).current,
    },
  });
}

export function winStreakWinLevel(observable: WinStreakObservable): void {
  const winStreakData = observable.getValue().winStreakActiveEvent;
  if (!winStreakData || winStreakData.finished) return;

  observable.updateValue({
    winStreakActiveEvent: {
      ...winStreakData,
      currentProgress: Math.min(winStreakData.config.levels - 1, winStreakData.currentProgress + 1),
    },
  });
}

export function winSteakNoteUserSawProgress(observable: WinStreakObservable): void {
  const winStreak = observable.getValue().winStreakActiveEvent;
  if (!winStreak) return;

  observable.updateValue({
    winStreakActiveEvent: {
      ...winStreak,
      lastSeenProgress: winStreak.currentProgress,
    },
  });
}

export function getChestToOpenIndex(state: WinStreakState): number {
  const { currentProgress, lastOpenedChestIndex } = state;
  const currentChestIndex = state.config.chests.findLastIndex(({ level }) => currentProgress >= level) ?? -1;
  if (currentChestIndex === -1) return -1;
  if (currentChestIndex <= lastOpenedChestIndex) return -1;
  return currentChestIndex;
}

export function openChest(observable: WinStreakObservable): ResourceMap | null {
  const state = observable.getValue().winStreakActiveEvent;
  if (!state) return null;

  const haveChest = getChestToOpenIndex(state) !== -1;
  if (!haveChest) return null;

  const chestData = state.config.chests[state.lastOpenedChestIndex + 1];
  const isLastChest = state.lastOpenedChestIndex + 1 === state.config.chests.length - 1;
  if (!chestData) return null;

  observable.updateValue({
    winStreakActiveEvent: {
      ...state,
      finished: isLastChest ? true : state.finished,
      lastOpenedChestIndex: state.lastOpenedChestIndex + 1,
    },
  });

  return chestData.rewards;
}

export function endCurrentEvent(observable: WinStreakObservable): void {
  const state = observable.getValue().winStreakActiveEvent;
  if (!state) return;

  observable.updateValue({
    winStreakActiveEvent: null,
    winStreakEventLastEndTime: Date.now(),
  });
}
